import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { BadgeCheck, Plus, RefreshCw, Users, Pencil, Trash2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";
import { useBadges, invalidateBadgeQueries, BadgeRow } from "@/hooks/useBadges";
import { useLearnerSearch } from "@/hooks/useLearnerSearch";
import { BADGE_COLORS, badgeHex } from "@/lib/badgeColors";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { UserAvatar } from "@/components/ui/UserAvatar";

function useBadgeAdminData() {
  return useQuery({
    queryKey: ["admin_badges"],
    queryFn: async () => {
      const [courses, curricula, holders] = await Promise.all([
        supabase.from("courses").select("id, title, badge_id").not("badge_id", "is", null),
        supabase.from("curricula").select("id, title, badge_id").not("badge_id", "is", null),
        supabase.from("user_badges").select("badge_id"),
      ]);
      const counts = new Map<string, number>();
      (holders.data || []).forEach((h) => counts.set(h.badge_id, (counts.get(h.badge_id) || 0) + 1));
      return { courses: courses.data || [], curricula: curricula.data || [], counts };
    },
  });
}

function errMsg(e: unknown) {
  return e instanceof Error ? e.message : "Something went wrong.";
}

export function BadgesManager() {
  const { user } = useAuth();
  const { toast } = useToast();
  const qc = useQueryClient();
  const { data: badges, isLoading } = useBadges();
  const { data: admin } = useBadgeAdminData();
  const [editing, setEditing] = useState<Partial<BadgeRow> | null>(null);
  const [holdersFor, setHoldersFor] = useState<BadgeRow | null>(null);
  const [saving, setSaving] = useState(false);

  const saveBadge = async () => {
    if (!editing?.name?.trim()) return;
    setSaving(true);
    try {
      const payload = {
        name: editing.name.trim(),
        description: editing.description?.trim() || null,
        color_key: editing.color_key || "blue",
        is_active: editing.is_active ?? true,
      };
      const { error } = editing.id
        ? await supabase.from("badges").update(payload).eq("id", editing.id)
        : await supabase.from("badges").insert({ ...payload, created_by: user?.id });
      if (error) throw error;
      toast({ title: editing.id ? "Badge updated" : "Badge created" });
      setEditing(null);
      invalidateBadgeQueries(qc);
    } catch (e) {
      toast({ title: "Could not save badge", description: errMsg(e), variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (b: BadgeRow) => {
    const { error } = await supabase.from("badges").update({ is_active: !b.is_active }).eq("id", b.id);
    if (error) return toast({ title: "Could not update badge", description: error.message, variant: "destructive" });
    toast({ title: b.is_active ? "Badge deactivated" : "Badge activated" });
    invalidateBadgeQueries(qc);
  };

  const rerun = async (b: BadgeRow) => {
    const { data, error } = await supabase.rpc("admin_rerun_badge", { _badge_id: b.id });
    if (error) return toast({ title: "Could not re-run awards", description: error.message, variant: "destructive" });
    toast({ title: "Awards re-run", description: `${data ?? 0} new holder(s).` });
    invalidateBadgeQueries(qc);
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-4">
        <div>
          <CardTitle className="flex items-center gap-2"><BadgeCheck className="h-5 w-5" /> Completion badges</CardTitle>
          <CardDescription>Attach badges to curricula or courses. Learners earn them automatically when they finish.</CardDescription>
        </div>
        <Button onClick={() => setEditing({ color_key: "blue", is_active: true })}>
          <Plus className="h-4 w-4 mr-1" /> New badge
        </Button>
      </CardHeader>
      <CardContent className="space-y-3">
        {isLoading && <p className="text-sm text-muted-foreground">Loading badges...</p>}
        {!isLoading && (badges || []).length === 0 && <p className="text-sm text-muted-foreground">No badges yet.</p>}
        {(badges || []).map((b) => {
          const attachedCourses = admin?.courses.filter((c) => c.badge_id === b.id) || [];
          const attachedCurricula = admin?.curricula.filter((c) => c.badge_id === b.id) || [];
          return (
            <div key={b.id} className="rounded-md border p-3 space-y-2">
              <div className="flex flex-wrap items-center gap-2">
                <BadgeCheck className="h-5 w-5" style={{ color: badgeHex(b.color_key) }} />
                <span className="font-medium">{b.name}</span>
                <Badge variant={b.is_active ? "default" : "secondary"}>{b.is_active ? "Active" : "Inactive"}</Badge>
                <span className="text-xs text-muted-foreground">{admin?.counts.get(b.id) || 0} holder(s)</span>
                <div className="ml-auto flex flex-wrap gap-2">
                  <Button size="sm" variant="outline" onClick={() => setHoldersFor(b)}><Users className="h-4 w-4 mr-1" />Holders</Button>
                  <Button size="sm" variant="outline" onClick={() => rerun(b)}><RefreshCw className="h-4 w-4 mr-1" />Re-run awards</Button>
                  <Button size="sm" variant="outline" onClick={() => setEditing(b)}><Pencil className="h-4 w-4 mr-1" />Edit</Button>
                  <div className="flex items-center gap-2 text-xs">
                    <Switch checked={b.is_active} onCheckedChange={() => toggleActive(b)} aria-label="Active" />
                  </div>
                </div>
              </div>
              {b.description && <p className="text-sm text-muted-foreground">{b.description}</p>}
              <p className="text-xs text-muted-foreground">
                Attached to:{" "}
                {attachedCurricula.length + attachedCourses.length === 0
                  ? "nothing yet"
                  : [
                      ...attachedCurricula.map((c) => `Curriculum: ${c.title}`),
                      ...attachedCourses.map((c) => `Course: ${c.title}`),
                    ].join(", ")}
              </p>
            </div>
          );
        })}
      </CardContent>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing?.id ? "Edit badge" : "New badge"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="badge-name">Name</Label>
              <Input id="badge-name" maxLength={80} value={editing?.name || ""} onChange={(e) => setEditing((p) => ({ ...p, name: e.target.value }))} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="badge-desc">Description</Label>
              <Textarea id="badge-desc" maxLength={500} rows={3} value={editing?.description || ""} onChange={(e) => setEditing((p) => ({ ...p, description: e.target.value }))} />
            </div>
            <div className="space-y-2">
              <Label>Colour</Label>
              <Select value={editing?.color_key || "blue"} onValueChange={(v) => setEditing((p) => ({ ...p, color_key: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {BADGE_COLORS.map((c) => (
                    <SelectItem key={c.key} value={c.key}>
                      <span className="inline-flex items-center gap-2">
                        <span className="h-3 w-3 rounded-full" style={{ backgroundColor: c.hex }} />{c.label}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <Switch id="badge-active" checked={editing?.is_active ?? true} onCheckedChange={(v) => setEditing((p) => ({ ...p, is_active: v }))} />
              <Label htmlFor="badge-active">Active</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={saveBadge} disabled={saving || !editing?.name?.trim()}>{saving ? "Saving..." : "Save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {holdersFor && <HoldersDialog badge={holdersFor} onClose={() => setHoldersFor(null)} />}
    </Card>
  );
}

function HoldersDialog({ badge, onClose }: { badge: BadgeRow; onClose: () => void }) {
  const { toast } = useToast();
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const { results, isLoading: searching } = useLearnerSearch(search);
  const [revoking, setRevoking] = useState<{ id: string; name: string } | null>(null);

  const { data: holders, isLoading } = useQuery({
    queryKey: ["admin_badges", "holders", badge.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("user_badges")
        .select("user_id, awarded_at, source_type, profiles!user_badges_user_id_fkey(full_name, avatar_url)")
        .eq("badge_id", badge.id)
        .order("awarded_at", { ascending: false });
      if (error) throw error;
      return data || [];
    },
  });

  const award = async (userId: string) => {
    const { error } = await supabase.rpc("admin_award_badge", { _user_id: userId, _badge_id: badge.id });
    if (error) return toast({ title: "Could not award badge", description: error.message, variant: "destructive" });
    toast({ title: "Badge awarded" });
    setSearch("");
    invalidateBadgeQueries(qc);
  };

  const revoke = async () => {
    if (!revoking) return;
    const { error } = await supabase.rpc("admin_revoke_badge", { _user_id: revoking.id, _badge_id: badge.id });
    if (error) toast({ title: "Could not revoke badge", description: error.message, variant: "destructive" });
    else toast({ title: "Badge revoked" });
    setRevoking(null);
    invalidateBadgeQueries(qc);
  };

  const holderIds = new Set((holders || []).map((h) => h.user_id));

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Holders of {badge.name}</DialogTitle></DialogHeader>
        <div className="space-y-2">
          <Label htmlFor="award-search">Award manually</Label>
          <Input id="award-search" placeholder="Search members by name or email" value={search} onChange={(e) => setSearch(e.target.value)} />
          {search.length >= 2 && (
            <div className="max-h-40 overflow-y-auto rounded-md border">
              {searching && <p className="p-2 text-xs text-muted-foreground">Searching...</p>}
              {!searching && results.length === 0 && <p className="p-2 text-xs text-muted-foreground">No members found.</p>}
              {results.map((r) => (
                <div key={r.id} className="flex items-center gap-2 p-2 border-b last:border-b-0">
                  <UserAvatar avatarPath={r.avatar_url} fullName={r.full_name} className="h-7 w-7" thumbnailSize={64} />
                  <span className="text-sm flex-1 truncate">{r.full_name || r.email}</span>
                  <Button size="sm" variant="outline" disabled={holderIds.has(r.id)} onClick={() => award(r.id)}>
                    {holderIds.has(r.id) ? "Has badge" : "Award"}
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="max-h-80 overflow-y-auto space-y-2">
          {isLoading && <p className="text-sm text-muted-foreground">Loading...</p>}
          {!isLoading && (holders || []).length === 0 && <p className="text-sm text-muted-foreground">No holders yet.</p>}
          {(holders || []).map((h) => {
            const p = h.profiles as unknown as { full_name: string | null; avatar_url: string | null } | null;
            return (
              <div key={h.user_id} className="flex items-center gap-2 rounded-md border p-2">
                <UserAvatar avatarPath={p?.avatar_url} fullName={p?.full_name} className="h-8 w-8" thumbnailSize={64} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{p?.full_name || "Unknown"}</p>
                  <p className="text-xs text-muted-foreground">
                    {format(new Date(h.awarded_at), "MMM d, yyyy")}, {h.source_type === "admin" ? "awarded by admin" : `completed ${h.source_type}`}
                  </p>
                </div>
                <Button size="icon" variant="ghost" aria-label="Revoke" onClick={() => setRevoking({ id: h.user_id, name: p?.full_name || "this member" })}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            );
          })}
        </div>
        <AlertDialog open={!!revoking} onOpenChange={(o) => !o && setRevoking(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Revoke badge?</AlertDialogTitle>
              <AlertDialogDescription>
                Remove {badge.name} from {revoking?.name}. Re-running awards will give it back if they still meet the requirements.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={revoke}>Revoke</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </DialogContent>
    </Dialog>
  );
}
